import React, { memo, useCallback, useMemo, MouseEvent } from 'react'
import { useHistory } from 'react-router-dom'
import Card from 'transfix-ui/components/Card'
import { makeStyles, Skeleton, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { HOMEPAGE_BOX_SHADOW } from 'apps/fleet-planner/pages/home/constants'
import { HelpIcon, RightArrowIcon } from 'transfix-icons'
import { HOW_THIS_WORKS_LINK } from '../../constants'
import { ITrackingType } from '../../types'
import LoadCount from '../LoadCount'

export interface IActiveShipmentsSummaryCardProps {
  /**
   * @default false
   */
  loading?: boolean
  activeLoadCount: number
  notTrackingCount: number
  onTimeCount: number
  runningLateCount: number
}

const useActiveShipmentsSummaryCardStyles = makeStyles(theme => ({
  cardLink: {
    height: '100%',
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(0.5),
    boxShadow: HOMEPAGE_BOX_SHADOW,
    height: '100%',
    '&:hover': {
      backgroundColor: brandColors.coolGray1,
      cursor: 'pointer',
    },
    '& .card-content': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  body: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  activeLoads: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',

    '& > div': {
      lineHeight: '1',
    },

    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  activeLoadsCount: {
    fontWeight: 700,
    [theme.breakpoints.up('md')]: {
      fontWeight: 'normal',
    },
  },
  activeLoadsCountSkeleton: {
    width: 30,
    height: 25,
    [theme.breakpoints.up('md')]: {
      width: 30,
      height: 19,
    },
  },
  loadCount: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    margin: `${theme.spacing(3)} 0px ${theme.spacing(2.5)}`,
    [theme.breakpoints.up('md')]: {
      margin: `${theme.spacing(2)} 0px ${theme.spacing(2.5)}`,
    },
  },
  help: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: brandColors.skyBlue6,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}))

const ActiveShipmentsSummaryCard = memo(
  ({
    activeLoadCount,
    loading = false,
    notTrackingCount,
    onTimeCount,
    runningLateCount,
  }: IActiveShipmentsSummaryCardProps) => {
    const history = useHistory()
    const { trackEvent } = useFMSAnalytics()
    const styles = useActiveShipmentsSummaryCardStyles()
    const { isMobile = false } = useBreakpointContext()

    const loadCountLabel = useMemo(() => {
      if (activeLoadCount === 1) {
        return 'Active Load'
      }
      return 'Active Loads'
    }, [activeLoadCount])

    const showCounts = useMemo(() => {
      if (loading) {
        return true
      }
      if (isMobile) {
        return activeLoadCount > 0
      }
      return true
    }, [activeLoadCount, isMobile, loading])

    const activeLoadClickHandler = useCallback(() => {
      trackEvent('Homepage', 'HOME_LOAD_SUMMARY_CLICKED_CARD')
      history.push('/loads')
    }, [history, trackEvent])

    const learnLinkClickHandler = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation()
        trackEvent('Homepage', 'HOME_LOAD_SUMMARY_CLICKED_LEARN_LINK')
        window.open(HOW_THIS_WORKS_LINK, '_blank')
      },
      [trackEvent]
    )

    return (
      <div
        className={styles.cardLink}
        onClick={activeLoadClickHandler}
        role='button'
        tabIndex={0}
        aria-hidden='true'>
        <Card className={styles.card}>
          <div className={styles.header}>
            <div className={styles.activeLoads}>
              {loading ? (
                <Skeleton variant='rectangular' className={styles.activeLoadsCountSkeleton} />
              ) : (
                <Typography variant='h4' component='div' className={styles.activeLoadsCount}>
                  {activeLoadCount}
                </Typography>
              )}
              <Typography variant='subtitle1' component='div' color='textPrimary'>
                {loadCountLabel}
              </Typography>
            </div>
            <RightArrowIcon size='large' color='coolGray5' />
          </div>
          {showCounts && (
            <div className={styles.body}>
              <div className={styles.loadCount}>
                <LoadCount
                  count={onTimeCount}
                  trackingType={ITrackingType.onTime}
                  loading={loading}
                />
                <LoadCount
                  count={runningLateCount}
                  trackingType={ITrackingType.runningLate}
                  loading={loading}
                />
                <LoadCount
                  count={notTrackingCount}
                  trackingType={ITrackingType.notTracking}
                  loading={loading}
                />
              </div>
              <div
                className={styles.help}
                onClick={learnLinkClickHandler}
                role='button'
                tabIndex={0}
                aria-hidden='true'>
                <HelpIcon size='large' />
                <Typography variant='button'>Learn how this works</Typography>
              </div>
            </div>
          )}
        </Card>
      </div>
    )
  }
)

export default ActiveShipmentsSummaryCard
